import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * Custom hook for automatic SEO optimizations
 * Handles dynamic schema updates, internal link tracking, and performance monitoring
 */
export const useSEOOptimization = () => {
  const location = useLocation();

  useEffect(() => {
    // Update page load time for performance tracking
    const loadTime = performance.now();
    
    // Log page view for analytics (could integrate with Google Analytics)
    if (typeof window !== 'undefined' && (window as any).gtag) {
      (window as any).gtag('event', 'page_view', {
        page_path: location.pathname,
        page_location: window.location.href,
        page_title: document.title,
      });
    }

    // Log performance metrics for monitoring
    if (loadTime > 3000) {
      console.warn(`Slow page load detected: ${loadTime}ms for ${location.pathname}`);
    }

  }, [location]);

  return {
    pathname: location.pathname,
    isHomePage: location.pathname === '/' || location.pathname === '/en',
    language: location.pathname.includes('/en/') ? 'en' : 'es'
  };
};

/**
 * Hook for adding internal link tracking and optimization
 */
export const useInternalLinkTracking = () => {
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const link = target.closest('a');
      
      if (link && link.hostname === window.location.hostname) {
        // Track internal link clicks
        const linkData = {
          from: window.location.pathname,
          to: link.pathname,
          text: link.textContent?.trim() || '',
          timestamp: new Date().toISOString()
        };
        
        // Could send to analytics
        console.log('Internal link click:', linkData);
      }
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);
};

/**
 * Hook for lazy loading images with SEO-friendly alt text validation
 */
export const useImageSEO = () => {
  useEffect(() => {
    // Check all images for alt text
    const images = document.querySelectorAll('img');
    const imagesWithoutAlt: string[] = [];
    
    images.forEach((img) => {
      if (!img.alt || img.alt.trim() === '') {
        imagesWithoutAlt.push(img.src);
      }
    });
    
    if (imagesWithoutAlt.length > 0) {
      console.warn(`Images without alt text found (${imagesWithoutAlt.length}):`, imagesWithoutAlt);
    }

    // Implement lazy loading for images below the fold
    const imageObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target as HTMLImageElement;
            if (img.dataset.src) {
              img.src = img.dataset.src;
              img.removeAttribute('data-src');
              imageObserver.unobserve(img);
            }
          }
        });
      },
      { rootMargin: '50px' }
    );

    // Observe images with data-src attribute
    document.querySelectorAll('img[data-src]').forEach((img) => {
      imageObserver.observe(img);
    });

    return () => imageObserver.disconnect();
  }, []);
};

/**
 * Hook for monitoring Core Web Vitals
 */
export const useWebVitals = () => {
  useEffect(() => {
    if ('web-vital' in window || typeof window === 'undefined') return;

    // Use requestIdleCallback to defer non-critical measurements
    const scheduleObservers = () => {
      const callback = () => {
        // Monitor Largest Contentful Paint (LCP)
        const observeLCP = () => {
          const observer = new PerformanceObserver((list) => {
            // Batch DOM reads using requestAnimationFrame
            requestAnimationFrame(() => {
              const entries = list.getEntries();
              const lastEntry = entries[entries.length - 1];
              
              if (lastEntry) {
                const lcp = lastEntry.startTime;
                console.log('LCP:', lcp.toFixed(2), 'ms');
                
                if (lcp > 2500) {
                  console.warn('LCP needs improvement (>2.5s)');
                }
              }
            });
          });

          try {
            observer.observe({ entryTypes: ['largest-contentful-paint'] });
          } catch (e) {
            console.warn('LCP observer not supported');
          }
          return observer;
        };

        // Monitor Cumulative Layout Shift (CLS)
        const observeCLS = () => {
          let clsValue = 0;
          const observer = new PerformanceObserver((list) => {
            // Batch calculations to avoid forced reflows
            requestAnimationFrame(() => {
              for (const entry of list.getEntries()) {
                if (!(entry as any).hadRecentInput) {
                  clsValue += (entry as any).value;
                }
              }
              
              if (clsValue > 0.1) {
                console.warn('CLS needs improvement:', clsValue.toFixed(3));
              }
            });
          });

          try {
            observer.observe({ entryTypes: ['layout-shift'] });
          } catch (e) {
            console.warn('CLS observer not supported');
          }
          return observer;
        };

        const lcpObserver = observeLCP();
        const clsObserver = observeCLS();

        return () => {
          lcpObserver?.disconnect();
          clsObserver?.disconnect();
        };
      };

      // Use requestIdleCallback if available, otherwise setTimeout
      if ('requestIdleCallback' in window) {
        const handle = requestIdleCallback(callback, { timeout: 2000 });
        return () => cancelIdleCallback(handle);
      } else {
        const timeout = setTimeout(callback, 1);
        return () => clearTimeout(timeout);
      }
    };

    const cleanup = scheduleObservers();

    return () => {
      cleanup?.();
    };
  }, []);
};
